import { Repair } from '../types/typesRepair';
import { Customer } from '../types/typesCustomer';
import { hybridApiBaseUrl, hybridFlags } from './hybridFlags';
import { saveRepairToHybrid } from './entitiesHybridService';
import hybridApiClient from './hybridApiClient';
import { auth } from '../firebaseConfig';

/** השרת שומר את הערך הקיים כשהשדות חסרים — כך עדכון מלא מהטופס לא דורס את «מי סלק» */
function withoutPaymentHandlerFields<T extends object>(repair: T): T {
  const copy = { ...repair } as Record<string, unknown>;
  delete copy.paymentHandledById;
  delete copy.paymentHandledByName;
  delete copy.paymentHandledManual;
  delete copy.paymentHandledManualAt;
  delete copy.paymentCheckoutHandlers;
  return copy as T;
}

interface PaymentParams {
  amount: number;
  currency?: string;
  description?: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  invoiceId?: string;
  callbackUrl?: string;
}

const VERIFONE_CHECKOUT_FUNCTION_URL =
  process.env.REACT_APP_VERIFONE_CHECKOUT_URL ||
  'https://us-central1-my-repair-lab.cloudfunctions.net/createVerifoneCheckout';

const VERIFONE_REFUND_URL =
  process.env.REACT_APP_VERIFONE_REFUND_URL ||
  'https://us-central1-my-repair-lab.cloudfunctions.net/verifoneRefund';
const VERIFONE_CANCEL_CAPTURE_URL =
  process.env.REACT_APP_VERIFONE_CANCEL_CAPTURE_URL ||
  (hybridFlags.useQnapRepairs || hybridFlags.disableFirestoreReads
    ? `${String(hybridApiBaseUrl).replace(/\/+$/, '')}/api/public/verifone-cancel-capture`
    : 'https://us-central1-my-repair-lab.cloudfunctions.net/verifoneCancelCapture');
const UPDATE_REPAIR_BILLING_URL =
  process.env.REACT_APP_UPDATE_REPAIR_BILLING_URL ||
  'https://us-central1-my-repair-lab.cloudfunctions.net/updateRepairBilling';

class PaymentService {
  private sessionFunctionUrl =
    process.env.REACT_APP_VERIFONE_SESSION_URL ||
    'https://us-central1-my-repair-lab.cloudfunctions.net/createVerifoneSessionHttp';

  private paymentMethod = (process.env.REACT_APP_PAYMENT_METHOD || 'iframe') as 'iframe' | 'redirect';

  private normalizePaymentUrl(rawUrl: string | undefined, sessionId: string) {
    if (rawUrl && typeof rawUrl === 'string') {
      try {
        const parsed = new URL(rawUrl);
        const isViewUrl = /\/view$/i.test(parsed.pathname) || parsed.pathname.includes('/view/');
        if (isViewUrl) {
          if (!parsed.searchParams.has('locale')) {
            parsed.searchParams.set('locale', 'he');
          }
          return parsed.toString();
        }
      } catch {
        // Ignore parsing errors and fall back below
      }
    }

    return `https://checkout.verifone.cloud/v2/checkout/${sessionId}/view?locale=he`;
  }

  private async callVerifoneSessionFunction(requestBody: Record<string, unknown>) {
    console.log('[PaymentService] Calling Verifone session function:', {
      url: this.sessionFunctionUrl,
      body: requestBody,
    });

    try {
      const response = await fetch(this.sessionFunctionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      console.log('[PaymentService] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[PaymentService] Error response:', errorText);
        let errorData: any;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || response.statusText };
        }
        throw new Error(errorData.error || `שגיאה ביצירת תשלום: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[PaymentService] Success response:', data);
      return data;
    } catch (error: any) {
      console.error('[PaymentService] Fetch error:', error);
      if (error.message) {
        throw error;
      }
      throw new Error(`שגיאה בחיבור לשרת: ${error.message || 'Unknown error'}`);
    }
  }

  async createPaymentSession(params: PaymentParams): Promise<{
    paymentUrl?: string;
    sessionId: string;
    iframeUrl?: string;
  }> {
    const amountValue =
      typeof params.amount === 'number' ? params.amount : parseFloat(String(params.amount));

    if (Number.isNaN(amountValue) || amountValue <= 0) {
      throw new Error('סכום תשלום לא תקין עבור Verifone');
    }

    const requestBody = {
      amount: {
        value: parseFloat(amountValue.toFixed(2)),
        currency: params.currency || 'ILS',
      },
      description: params.description || 'תשלום תיקון',
      reference: params.invoiceId || `repair_${Date.now()}`,
      customer: {
        name: params.customerName,
        email: params.customerEmail,
        phone: params.customerPhone,
      },
      returnUrl: params.callbackUrl || `${window.location.origin}/payment-callback?session={sessionId}`,
      locale: 'he',
      metadata: {
        invoiceId: params.invoiceId || '',
      },
    };

    try {
      const data = await this.callVerifoneSessionFunction(requestBody);

      if (!data?.success || !data?.sessionId) {
        throw new Error(data?.error || 'תגובה לא תקינה מ-Firebase Functions בעת יצירת תשלום');
      }

      const normalizedUrl = this.normalizePaymentUrl(
        data.paymentUrl || data.viewUrl || data.url,
        data.sessionId,
      );

      if (this.paymentMethod === 'iframe') {
        return {
          iframeUrl: normalizedUrl,
          sessionId: data.sessionId,
        };
      }

      return {
        paymentUrl: normalizedUrl,
        sessionId: data.sessionId,
      };
    } catch (error: any) {
      console.error('Error creating payment session via Firebase Functions:', error);
      throw new Error(error?.message || 'שגיאה ביצירת תשלום. בדוק שהפונקציה createVerifoneSessionHttp זמינה.');
    }
  }

  async createPaymentFromRepair(repair: Repair, customer: Customer | null): Promise<{
    paymentUrl?: string;
    sessionId: string;
    iframeUrl?: string;
  }> {
    if (!customer) {
      throw new Error('לקוח לא נמצא');
    }

    if (!repair.paymentAmount || repair.paymentAmount <= 0) {
      throw new Error('סכום תשלום לא תקין');
    }

    const customerEmail = customer.email || '';
    // Email is optional for Verifone payment, but recommended for invoice

    const firstName = String(customer.firstName || '').trim();
    const lastName = String(customer.lastName || '').trim();
    const customerName =
      [firstName, lastName].filter(Boolean).join(' ').trim() ||
      String(customer.name || '').trim() ||
      String(repair.customerName || '').trim() ||
      'לקוח';

    if (!customerName || customerName.trim() === '') {
      throw new Error('שם לקוח חסר - נדרש ליצירת תשלום');
    }

    console.log('[PaymentService] Creating payment session:', {
      amount: repair.paymentAmount,
      customerName,
      customerEmail,
      customerPhone: customer.phone || '',
      callNumber: repair.callNumber,
    });

    return this.createPaymentSession({
      amount: repair.paymentAmount,
      currency: 'ILS',
      description: `תיקון ${repair.deviceModel || repair.model || ''} - קריאה ${repair.callNumber || ''}`,
      customerName,
      customerEmail,
      customerPhone: customer.phone || '',
      invoiceId: repair.callNumber || undefined,
      callbackUrl: `${window.location.origin}/payment-callback?repairId=${repair.id}`,
    });
  }

  async checkPaymentStatus(sessionId: string): Promise<{
    status: 'pending' | 'completed' | 'failed' | 'cancelled';
    transactionId?: string;
    invoiceUrl?: string;
  }> {
    return {
      status: 'pending',
      transactionId: sessionId,
    };
  }

  /**
   * Verifone Checkout (מדריך docs/VERIFONE-CHECKOUT-GUIDE.md): יוצר Checkout Session ומחזיר URL להפניה.
   * @param installments אופציונלי: כמות תשלומים קבועה (2–24). אם לא נשלח – הלקוח בוחר בדף Verifone.
   */
  async createVerifoneCheckoutFromRepair(
    repair: Repair,
    customer: Customer | null,
    installments?: number,
    options?: { paymentSource?: string },
  ): Promise<{ redirectUrl: string }> {
    const repairId = (repair.id || '').trim();
    if (!repairId) throw new Error('יש לשמור את הקריאה לפני פתיחת דף תשלום (חסר מזהה קריאה).');
    const amount = Number(repair.paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) throw new Error('סכום תשלום לא תקין');
    const firstName = String(customer?.firstName || '').trim();
    const lastName = String(customer?.lastName || '').trim();
    const fullNameFromParts = [firstName, lastName].filter(Boolean).join(' ').trim();
    const customerName =
      fullNameFromParts ||
      String(customer?.name || '').trim() ||
      String(repair.customerName || '').trim() ||
      'לקוח';
    if (!customerName?.trim()) throw new Error('שם לקוח חסר');

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const paymentSource = String(options?.paymentSource || '').trim();
    const sourceQuery = paymentSource ? `&paymentSource=${encodeURIComponent(paymentSource)}` : '';
    const baseCallback = repair.callNumber
      ? `${origin}/payment-callback?repairId=${encodeURIComponent(repairId)}&callNumber=${encodeURIComponent(repair.callNumber)}${sourceQuery}`
      : `${origin}/payment-callback?repairId=${encodeURIComponent(repairId)}${sourceQuery}`;
    // לא מוסיפים &status=success – Verifone מוסיף את התוצאה האמיתית (result/outcome) בכתובת החזרה
    // אחרי ביטול לכידה – Verifone דורש merchant_reference ייחודי; שולחים סופיקס כדי לאפשר תשלום מחדש. Verifone דורש שלא יהיה ריק.
    const isRetryAfterVoid = repair.paymentStatus === 'capture_cancelled';
    const orderRef = isRetryAfterVoid ? `${repairId}-${Date.now()}` : repairId;

    const callRef = String(repair.callNumber || '').replace(/[^A-Za-z0-9]/g, '');
    const body: Record<string, unknown> = {
      amount,
      currency: 'ILS',
      description: this.buildVerifoneDynamicDescriptor(
        callRef ? `REPAIR ${callRef}` : 'REPAIR',
        'REPAIR',
      ),
      repairId,
      orderRef,
      successUrl: baseCallback,
      cancelUrl: baseCallback,
      errorUrl: baseCallback,
      customer: {
        name: customerName.trim(),
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: customer?.email || undefined,
        phone: customer?.phone || repair.customerPhone || undefined,
        ...(customer?.city && { city: customer.city }),
      },
    };
    if (typeof installments === 'number' && installments > 1) body.installments = Math.min(24, Math.max(2, Math.round(installments)));

    const res = await fetch(VERIFONE_CHECKOUT_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errText = await res.text();
      let errJson: { error?: string; invalidParameters?: Record<string, unknown> } = {};
      try {
        errJson = JSON.parse(errText);
      } catch {
        errJson = { error: errText };
      }
      const invalid = errJson.invalidParameters;
      const detailMsg = invalid && typeof invalid === 'object'
        ? ` פרמטרים לא תקינים: ${JSON.stringify(invalid)}`
        : '';
      throw new Error((errJson.error || `שגיאה ${res.status} ביצירת דף תשלום`) + detailMsg);
    }

    const data = await res.json();
    if (!data?.redirectUrl) throw new Error('לא התקבל קישור תשלום מהשרת');
    let url = data.redirectUrl;
    try {
      const parsed = new URL(url);
      if (parsed.pathname.includes('/view')) {
        parsed.searchParams.set('locale', 'he');
        url = parsed.toString();
      }
    } catch {
      // use url as is
    }
    if (paymentSource !== 'customer_track') {
      await this.recordCurrentUserAsCheckoutHandler(repairId, url);
    }
    return { redirectUrl: url };
  }

  /** המשתמש המחובר שיצר את דף התשלום — נרשם על ה-checkout הספציפי */
  private async recordCurrentUserAsCheckoutHandler(repairId: string, redirectUrl: string): Promise<void> {
    const current = auth.currentUser;
    if (!current?.uid) return;
    const { checkoutId } = this.extractCheckoutSessionFromRedirectUrl(redirectUrl);
    try {
      await this.setPaymentHandler(repairId, {
        handledById: current.uid,
        handledByName: String(current.displayName || current.email || '').trim(),
        checkoutId,
      });
    } catch (e) {
      console.warn('[PaymentService] recording payment handler failed', e);
    }
  }

  /**
   * «מי סלק» — נרשם לכל דף תשלום שנפתח (checkoutId), או נבחר ידנית (manual).
   * ה-callback של Verifone משייך את הגבייה למי שיצר את הדף ששולם בפועל.
   */
  async setPaymentHandler(
    repairId: string,
    data: { handledById: string; handledByName: string; checkoutId?: string; manual?: boolean },
  ): Promise<Repair | null> {
    const id = String(repairId || '').trim();
    if (!id) return null;
    if (!(hybridFlags.useQnapRepairs || hybridFlags.disableFirestoreReads)) return null;
    const response = await hybridApiClient.post(`/api/repairs/${encodeURIComponent(id)}/payment-handler`, data);
    return (response?.data || null) as Repair | null;
  }

  /** יצירת תשלום ממעקב קריאות ציבורי (בלי התחברות למעבדה) */
  async createVerifoneCheckoutFromPublicTrack(repair: Repair): Promise<{ redirectUrl: string }> {
    const apiBase = String(hybridApiBaseUrl || 'https://api.gamestar.co.il').replace(/\/+$/, '');
    try {
      await fetch(`${apiBase}/api/public/track-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          repairId: repair.id || '',
          callNumber: repair.callNumber || '',
        }),
      });
    } catch {
      // לא חוסם יצירת checkout
    }
    return this.createVerifoneCheckoutFromRepair(repair, null, undefined, {
      paymentSource: 'customer_track',
    });
  }

  /** Verifone dynamic_descriptor: עד 25 תווים, ASCII + +_-,. בלבד */
  private buildVerifoneDynamicDescriptor(label: string, fallback = 'PAYMENT'): string {
    const MAX = 25;
    const sanitized = String(label || '')
      .replace(/[^A-Za-z0-9 +_\-,.]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const value = sanitized || fallback;
    return value.length <= MAX ? value : value.slice(0, MAX);
  }

  /** Verifone merchant_reference – מקסימום 50 תווים */
  private buildSupplementVerifoneOrderRef(repairId: string): string {
    const MAX = 50;
    const rid = String(repairId || '').trim();
    const suffix = `s${Date.now()}`;
    if (!rid) return suffix.slice(0, MAX);
    const budget = MAX - 1 - suffix.length;
    const prefix = rid.length <= budget ? rid : rid.slice(0, budget);
    return `${prefix}-${suffix}`;
  }

  private isCreditPaymentRepair(repair: Repair): boolean {
    const method = String(repair.paymentMethod || '').trim().toLowerCase();
    const gatewayPaid = Number(repair.paymentGatewayPaidAmount || 0);
    return (
      method.includes('אשראי') ||
      method.includes('credit') ||
      method.includes('card') ||
      gatewayPaid > 0 ||
      String((repair as Repair & { paymentGateway?: string }).paymentGateway || '')
        .trim()
        .toLowerCase() === 'verifone'
    );
  }

  /**
   * שומר pendingSupplement דרך PATCH קריאה — עובד גם כש-endpoint ייעודי עדיין לא deployed בענן.
   */
  private async prepareSupplementPaymentViaRepairPatch(
    repair: Repair,
    amount: number,
    description?: string,
  ): Promise<{ success: boolean; orderRef: string; amount: number }> {
    const repairId = String(repair.id || '').trim();
    if (!repairId) throw new Error('חסר מזהה קריאה');
    if (String(repair.paymentStatus || '') !== 'completed') {
      throw new Error('תוספת תשלום מותרת רק לקריאה שכבר שולמה באשראי');
    }
    if (!this.isCreditPaymentRepair(repair)) {
      throw new Error('תוספת תשלום זמינה רק לתשלומי אשראי');
    }

    const roundedAmount = Math.round(amount * 100) / 100;
    const orderRef = this.buildSupplementVerifoneOrderRef(repairId);
    const nowIso = new Date().toISOString();
    const desc = String(description || '').trim();

    await saveRepairToHybrid({
      ...repair,
      pendingSupplement: {
        amount: roundedAmount,
        description: desc,
        orderRef,
        createdAt: nowIso,
      },
      updatedAt: nowIso,
    });

    return { success: true, orderRef, amount: roundedAmount };
  }

  /**
   * הכנת תוספת תשלום באשראי – שומר pendingSupplement בשרת ומחזיר orderRef ייחודי ל-Verifone.
   */
  async prepareSupplementPayment(
    repair: Repair,
    amount: number,
    description?: string,
  ): Promise<{ success: boolean; orderRef: string; amount: number }> {
    if (!(hybridFlags.useQnapRepairs || hybridFlags.disableFirestoreReads)) {
      throw new Error('תוספת תשלום זמינה כרגע בסביבת NAS בלבד');
    }
    const repairId = String(repair.id || '').trim();
    if (!repairId) throw new Error('חסר מזהה קריאה');
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      throw new Error('סכום תוספת לא תקין');
    }
    try {
      const response = await hybridApiClient.post('/api/public/prepare-supplement-payment', {
        repairId,
        amount: parsedAmount,
        description: String(description || '').trim() || undefined,
      });
      return response.data as { success: boolean; orderRef: string; amount: number };
    } catch (error: any) {
      const status = Number(error?.response?.status || 0);
      const apiError = String(error?.response?.data?.error || '').trim();
      // api.gamestar.co.il עדיין לא כולל את ה-endpoint הייעודי — fallback ל-PATCH קריאה.
      if (status === 404 && apiError === 'Not found') {
        return this.prepareSupplementPaymentViaRepairPatch(repair, parsedAmount, description);
      }
      const message =
        apiError ||
        String(error?.message || '').trim() ||
        'שגיאה בהכנת תוספת תשלום';
      throw new Error(message);
    }
  }

  /**
   * Verifone Checkout לתוספת תשלום – orderRef ייחודי, callback עם paymentKind=supplement.
   */
  async createVerifoneSupplementCheckout(
    repair: Repair,
    customer: Customer | null,
    amount: number,
    options: { orderRef: string; description?: string },
  ): Promise<{ redirectUrl: string }> {
    const repairId = (repair.id || '').trim();
    if (!repairId) throw new Error('יש לשמור את הקריאה לפני פתיחת תשלום תוספת');
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) throw new Error('סכום תוספת לא תקין');
    const orderRef = String(options.orderRef || '').trim();
    if (!orderRef) throw new Error('חסר מזהה עסקת תוספת');

    const firstName = String(customer?.firstName || '').trim();
    const lastName = String(customer?.lastName || '').trim();
    const fullNameFromParts = [firstName, lastName].filter(Boolean).join(' ').trim();
    const customerName =
      fullNameFromParts ||
      String(customer?.name || '').trim() ||
      String(repair.customerName || '').trim() ||
      'לקוח';

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const baseCallback = repair.callNumber
      ? `${origin}/payment-callback?repairId=${encodeURIComponent(repairId)}&callNumber=${encodeURIComponent(repair.callNumber)}&paymentKind=supplement`
      : `${origin}/payment-callback?repairId=${encodeURIComponent(repairId)}&paymentKind=supplement`;

    const callRef =
      String(repair.callNumber || repairId.slice(0, 8)).replace(/[^A-Za-z0-9]/g, '') || 'X';
    const description = this.buildVerifoneDynamicDescriptor(`SUPP ${callRef}`, 'SUPP');

    const body: Record<string, unknown> = {
      amount: parsedAmount,
      currency: 'ILS',
      description,
      repairId,
      orderRef,
      successUrl: baseCallback,
      cancelUrl: baseCallback,
      errorUrl: baseCallback,
      customer: {
        name: customerName.trim(),
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: customer?.email || undefined,
        phone: customer?.phone || repair.customerPhone || undefined,
        ...(customer?.city && { city: customer.city }),
      },
    };

    const res = await fetch(VERIFONE_CHECKOUT_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errText = await res.text();
      let errJson: { error?: string; invalidParameters?: Record<string, unknown> } = {};
      try {
        errJson = JSON.parse(errText);
      } catch {
        errJson = { error: errText };
      }
      const invalid = errJson.invalidParameters;
      const detailMsg = invalid && typeof invalid === 'object'
        ? ` פרמטרים לא תקינים: ${JSON.stringify(invalid)}`
        : '';
      throw new Error((errJson.error || `שגיאה ${res.status} ביצירת דף תשלום תוספת`) + detailMsg);
    }

    const data = await res.json();
    if (!data?.redirectUrl) throw new Error('לא התקבל קישור תשלום מהשרת');
    let url = data.redirectUrl;
    try {
      const parsed = new URL(url);
      if (parsed.pathname.includes('/view')) {
        parsed.searchParams.set('locale', 'he');
        url = parsed.toString();
      }
    } catch {
      // use url as is
    }
    await this.recordCurrentUserAsCheckoutHandler(repairId, url);
    return { redirectUrl: url };
  }

  private extractCheckoutSessionFromRedirectUrl(redirectUrl: string): {
    checkoutId: string;
    checkoutUrl: string;
  } {
    const checkoutUrl = String(redirectUrl || '').trim();
    const match = checkoutUrl.match(/\/v2\/checkout\/([^/?#]+)/i);
    return { checkoutId: match ? match[1] : '', checkoutUrl };
  }

  /** שומר מזהה checkout בקריאה – מאפשר סנכרון מול Verifone אם ה-callback לא עדכן את ה-DB */
  async persistCheckoutSessionOnRepair(repair: Repair, redirectUrl: string): Promise<void> {
    const { checkoutId, checkoutUrl } = this.extractCheckoutSessionFromRedirectUrl(redirectUrl);
    const repairId = (repair.id || '').trim();
    if (!repairId || !checkoutId) return;
    const merged = withoutPaymentHandlerFields({
      ...repair,
      paymentGatewayCheckoutUrl: checkoutUrl,
      paymentGatewayCheckoutId: checkoutId,
    } as Repair & { paymentGatewayCheckoutUrl?: string; paymentGatewayCheckoutId?: string });
    if (hybridFlags.useQnapRepairs || hybridFlags.disableFirestoreReads) {
      await saveRepairToHybrid(merged as Repair);
    }
  }

  /**
   * שואל את Verifone (דרך שרת NAS) ומעדכן את הקריאה אם העסקה אושרה – לתיקון מקרים שה-callback נכשל.
   */
  async syncVerifonePayment(
    repairId: string,
    callNumber?: string,
    verifoneId?: string,
  ): Promise<{
    success: boolean;
    paymentStatus?: string;
    paymentCompletedAt?: string;
    paymentHistory?: Repair['paymentHistory'];
    error?: string;
    alreadyCompleted?: boolean;
  }> {
    if (!(hybridFlags.useQnapRepairs || hybridFlags.disableFirestoreReads)) {
      return { success: false, error: 'סנכרון Verifone זמין רק במצב NAS' };
    }
    try {
      const response = await hybridApiClient.post('/api/public/verifone-sync-payment', {
        repairId,
        callNumber: callNumber || undefined,
        verifoneId: verifoneId || undefined,
        checkoutId: verifoneId || undefined,
      });
      return response.data as {
        success: boolean;
        paymentStatus?: string;
        paymentCompletedAt?: string;
        paymentHistory?: Repair['paymentHistory'];
        error?: string;
        alreadyCompleted?: boolean;
      };
    } catch (error: any) {
      const message =
        String(error?.response?.data?.error || '').trim() ||
        String(error?.message || '').trim() ||
        'שגיאה בסנכרון תשלום';
      return { success: false, error: message };
    }
  }

  /**
   * סנכרון שדות חיוב (חברה, שם פרטי, משפחה) רגע לפני תשלום – לוורדפרס/webhook.
   * במצב NAS/Postgres: PATCH ל-API (אותו מקור אמת כמו האפליקציה). אחרת: Cloud Function ל-Firestore.
   */
  async syncRepairBillingBeforePayment(
    repair: Repair,
    data: {
      company?: string;
      customerFirstName?: string;
      customerLastName?: string;
      paymentHandledById?: string;
      paymentHandledByName?: string;
    }
  ): Promise<void> {
    if (hybridFlags.useQnapRepairs) {
      const rid = (repair.id || '').trim();
      if (!rid) {
        console.warn('[PaymentService] syncRepairBilling (NAS): חסר repair.id');
        return;
      }
      const merged = { ...repair } as Repair & {
        customerFirstName?: string;
        customerLastName?: string;
        paymentHandledById?: string;
        paymentHandledByName?: string;
      };
      if (data.company !== undefined) merged.customerCompany = String(data.company ?? '').trim();
      if (data.customerFirstName !== undefined) {
        merged.customerFirstName = String(data.customerFirstName ?? '').trim();
      }
      if (data.customerLastName !== undefined) {
        merged.customerLastName = String(data.customerLastName ?? '').trim();
      }
      // «מי סלק» נרשם בנפרד (setPaymentHandler) — לא שולחים ערך ישן מהטופס שידרוס אותו
      await saveRepairToHybrid(withoutPaymentHandlerFields(merged) as Repair);
      return;
    }

    const repairId = (repair.id || '').trim();
    if (!repairId) {
      console.warn('[PaymentService] syncRepairBilling: חסר repair.id');
      return;
    }
    const res = await fetch(UPDATE_REPAIR_BILLING_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repairId,
        company: (data.company ?? '').trim() || undefined,
        customerFirstName: (data.customerFirstName ?? '').trim() || undefined,
        customerLastName: (data.customerLastName ?? '').trim() || undefined,
        paymentHandledById: (data.paymentHandledById ?? '').trim() || undefined,
        paymentHandledByName: (data.paymentHandledByName ?? '').trim() || undefined,
      }),
    });
    if (!res.ok) {
      const text = await res.text();
      console.warn('[PaymentService] syncRepairBilling failed:', res.status, text);
    }
  }

  /**
   * החזר כספי (ממיום למחרת הסליקה). קורא ל-Cloud Function שמעדכן Firestore.
   */
  async refund(repairId: string, amount: number): Promise<{
    success: boolean;
    paymentRefundedAmount?: number;
    paymentAmount?: number;
    paymentStatus?: string;
    paymentRefundedAt?: string;
  }> {
    if (hybridFlags.useQnapRepairs || hybridFlags.disableFirestoreReads) {
      try {
        const response = await hybridApiClient.post('/api/public/verifone-refund', {
          repairId,
          amount,
        });
        return response.data as {
          success: boolean;
          paymentRefundedAmount?: number;
          paymentAmount?: number;
          paymentStatus?: string;
          paymentRefundedAt?: string;
        };
      } catch (error: any) {
        const message =
          String(error?.response?.data?.error || '').trim() ||
          String(error?.message || '').trim() ||
          'שגיאה בביצוע החזר';
        throw new Error(message);
      }
    }

    const res = await fetch(VERIFONE_REFUND_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repairId, amount }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'שגיאה בביצוע החזר');
    return data;
  }

  /**
   * תיעוד בדיעבד של החזר שכבר בוצע בפועל ישירות ב-Verifone Central (למשל כשכפתור
   * "החזר כספי" נכשל וההחזר בוצע ידנית מחוץ למערכת). לא מבצע החזר חדש מול Verifone -
   * רק מעדכן את הרישום אצלנו. מוגבל למנהלים בלבד בשרת.
   */
  async recordManualRefund(repairId: string, amount: number, note?: string): Promise<{
    success: boolean;
    paymentRefundedAmount?: number;
    paymentAmount?: number;
    paymentStatus?: string;
    paymentRefundedAt?: string;
  }> {
    try {
      const response = await hybridApiClient.post('/api/public/verifone-record-manual-refund', {
        repairId,
        amount,
        note: (note || '').trim() || undefined,
      });
      return response.data as {
        success: boolean;
        paymentRefundedAmount?: number;
        paymentAmount?: number;
        paymentStatus?: string;
        paymentRefundedAt?: string;
      };
    } catch (error: any) {
      const message =
        String(error?.response?.data?.error || '').trim() ||
        String(error?.message || '').trim() ||
        'שגיאה בתיעוד ההחזר';
      throw new Error(message);
    }
  }

  /**
   * בדיקה בלבד (לא כותבת כלום) - האם Verifone מדווח שהעסקה של הקריאה הזו כבר הוחזרה.
   * משמש למילוי אוטומטי של דיאלוג "תעד החזר ידני" במקום שהמנהל יזין הכל בעצמו.
   */
  async checkRefundStatus(repairId: string): Promise<{
    success: boolean;
    checked?: boolean;
    isRefunded?: boolean;
    refundedAmount?: number;
    rawStatus?: string;
    reason?: string;
  }> {
    try {
      const response = await hybridApiClient.post('/api/public/verifone-check-refund-status', { repairId });
      return response.data;
    } catch (error: any) {
      const message =
        String(error?.response?.data?.error || '').trim() ||
        String(error?.message || '').trim() ||
        'שגיאה בבדיקה מול Verifone';
      throw new Error(message);
    }
  }

  /**
   * ביטול לכידה (void) – באותו יום שבוצעה הסליקה. קורא ל-Cloud Function שמעדכן Firestore.
   */
  async cancelCapture(repairId: string): Promise<{
    success: boolean;
    paymentStatus?: string;
    revertedFalseMark?: boolean;
    paymentHistory?: Repair['paymentHistory'];
  }> {
    if (hybridFlags.useQnapRepairs || hybridFlags.disableFirestoreReads) {
      try {
        const response = await hybridApiClient.post('/api/public/verifone-cancel-capture', { repairId });
        return response.data as {
          success: boolean;
          paymentStatus?: string;
          revertedFalseMark?: boolean;
          paymentHistory?: Repair['paymentHistory'];
        };
      } catch (error: any) {
        const message =
          String(error?.response?.data?.error || '').trim() ||
          String(error?.message || '').trim() ||
          'שגיאה בביטול הלכידה';
        throw new Error(message);
      }
    }

    const res = await fetch(VERIFONE_CANCEL_CAPTURE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repairId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'שגיאה בביטול הלכידה');
    return data;
  }

  /**
   * סנכרון ידני לוורדפרס/ווקומרס להפקת חשבונית (למקרה שכבר שולם ורוצים לשלוח שוב).
   * כולל תוספות תשלום שעדיין לא נוצרה להן הזמנה נפרדת בוורדפרס.
   */
  async syncInvoiceToWordPress(
    repairId: string,
    options?: { force?: boolean; syncSupplements?: boolean; supplementOnly?: boolean },
  ): Promise<{
    success: boolean;
    syncResult?: {
      provider?: string;
      orderId?: string;
      idempotent?: boolean;
      action?: 'created' | 'found_in_woo' | 'already_in_repair' | 'unknown';
    };
    supplementSyncResults?: Array<{
      amount: number;
      transactionId?: string;
      ok: boolean;
      error?: string;
      syncResult?: {
        provider?: string;
        orderId?: string;
        idempotent?: boolean;
        action?: string;
      };
    }>;
  }> {
    if (!repairId || !String(repairId).trim()) {
      throw new Error('חסר מזהה קריאה');
    }
    if (hybridFlags.useQnapRepairs || hybridFlags.disableFirestoreReads) {
      try {
        // יצירת הזמנה בוורדפרס (sleep + קמה) יכולה לקחת 15–30 שניות; LAN timeout ברירת מחדל = 8s
        const response = await hybridApiClient.post(
          '/api/public/wordpress-sync-invoice',
          {
            repairId,
            force: Boolean(options?.force),
            syncSupplements: options?.syncSupplements !== false,
            supplementOnly: Boolean(options?.supplementOnly),
          },
          {
          timeout: 90000,
          },
        );
        return response.data as {
          success: boolean;
          syncResult?: {
            provider?: string;
            orderId?: string;
            idempotent?: boolean;
            action?: 'created' | 'found_in_woo' | 'already_in_repair' | 'unknown';
          };
          supplementSyncResults?: Array<{
            amount: number;
            transactionId?: string;
            ok: boolean;
            error?: string;
            syncResult?: {
              provider?: string;
              orderId?: string;
              idempotent?: boolean;
              action?: string;
            };
          }>;
        };
      } catch (error: any) {
        const message =
          String(error?.response?.data?.error || '').trim() ||
          String(error?.message || '').trim() ||
          'שגיאה בסנכרון לוורדפרס';
        throw new Error(message);
      }
    }
    throw new Error('סנכרון ידני זמין כרגע בסביבת NAS בלבד');
  }

  /**
   * שליחת מייל עם קישור תשלום ללקוח (דרך Cloud Function + Resend). שולח ישירות ללא פתיחת חלון.
   */
  async sendPaymentLinkByEmail(to: string, subject: string, html: string): Promise<{ success: true; id?: string }> {
    const { httpsCallable } = await import('firebase/functions');
    const { getFirebaseFunctions } = await import('../firebaseConfig');
    const sendEmail = httpsCallable<
      { to: string; subject: string; html: string },
      { success: boolean; id?: string }
    >(getFirebaseFunctions(), 'sendPaymentLinkEmail');
    const result = await sendEmail({ to: to.trim(), subject, html });
    const data = result.data as { success: boolean; id?: string } | undefined;
    if (!data?.success) throw new Error('שליחת המייל נכשלה');
    return { success: true, id: data.id };
  }
}

export const paymentService = new PaymentService();
export default paymentService;


